<template>
  <div
    class="fixed bottom-0 left-0 w-screen xl:w-[400px] h-screen xl:h-auto z-[100002] opacity-0 pointer-events-none transition-all duration-500"
    id="tutco-immediate-assistance-modal"
    :class="{ 'opacity-100 pointer-events-auto xl:left-10 xl:bottom-5': state.isModalVisible }">
    <div
      class="absolute top-0 left-0 w-full h-full z-10 opacity-70 bg-black backdrop-blur-lg xl:hidden"
      @click="closeModal"></div>
    <div
      class="absolute xl:relative left-0 -translate-x-full scale-0 w-[530px] xl:w-full max-w-full h-full xl:h-auto z-20 flex items-center transition-all duration-500"
      :class="{ 'left-1/2 xl:left-0 !-translate-x-1/2 xl:!translate-x-0 scale-100': state.isModalVisible }">
      <div class="relative w-full h-fit max-h-full bg-white overflow-y-auto rounded-2xl shadow-[0_0_15px_rgba(0,0,0,0.2)]">
        <div
          class="absolute top-2 right-2 z-10 cursor-pointer group"
          @click="closeModal">
          <svg
            width="24"
            height="25"
            viewBox="0 0 24 25"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            class="block w-5 h-auto">
            <path
              d="M22.9999 1.35556L1.00132 23.3542"
              stroke="#553F3F"
              stroke-width="2"
              stroke-linecap="round"
              class="transition-all duration-300 group-hover:stroke-red" />
            <path
              d="M22.9999 23.3554L1.00132 1.35678"
              stroke="#553F3F"
              stroke-width="2"
              stroke-linecap="round"
              class="transition-all duration-300 group-hover:stroke-red" />
          </svg>
        </div>
        <div class="w-full p-4">
          <div
            v-if="!state.isSuccessInfoVisible"
            class="w-full flex flex-col">
            <div class="w-full flex justify-center">
              <img
                :src="GLOBAL_CURRENT_COMPANY_LOGO"
                :alt="`${GLOBAL_CURRENT_COMPANY_TITLE} logo`"
                :title="`${GLOBAL_CURRENT_COMPANY_TITLE} logo`"
                class="lazy block w-auto max-w-96 h-auto max-h-20 mb-2"
                loading="lazy" />
            </div>
            <div class="w-full text-xl font-medium text-center mb-1">Is Immediate Assistance Required?</div>
            <div class="w-full text-lg leading-tight font-light text-center mb-3">
              Please complete this form, and get the fastest delivery in industry.
            </div>
            <div class="w-full flex flex-col items-center gap-2">
              <InputText
                :label="!state.isDesktop ? 'Full Name*' : ''"
                id="ia-full-name"
                name="fullName"
                :placeholder="state.isDesktop ? 'Full Name*' : ''"
                v-model:value="state.form.fullName"
                :isRequired="true"
                :maxLength="255"
                :triggerValidation="state.triggerFormValidationCounter"
                :serverError="state.errors.fullName"
                @check-is-valid="({ name, isValid }) => updateValidation(name, isValid)" />

              <InputEmail
                :label="!state.isDesktop ? 'Email*' : ''"
                id="ia-email"
                name="email"
                :placeholder="state.isDesktop ? 'Email*' : ''"
                v-model:value="state.form.email"
                :isRequired="true"
                :maxLength="254"
                :triggerValidation="state.triggerFormValidationCounter"
                :serverError="state.errors.email"
                @check-is-valid="({ name, isValid }) => updateValidation(name, isValid)" />

              <InputPhoneNumber
                :label="!state.isDesktop ? 'Phone*' : ''"
                id="ia-phone"
                name="phone"
                :placeholder="state.isDesktop ? 'Phone*' : ''"
                v-model:value="state.form.phone"
                :isRequired="true"
                :maxLength="30"
                :triggerValidation="state.triggerFormValidationCounter"
                :serverError="state.errors.phone"
                @check-is-valid="({ name, isValid }) => updateValidation(name, isValid)" />

              <TextAreaComponent
                :label="!state.isDesktop ? 'Comments' : ''"
                id="ia-comments"
                name="comments"
                :placeholder="state.isDesktop ? 'Comments' : ''"
                v-model:value="state.form.comments"
                :isRequired="false"
                :maxLength="2000"
                :triggerValidation="state.triggerFormValidationCounter"
                @check-is-valid="({ name, isValid }) => updateValidation(name, isValid)" />

              <div
                class="w-full text-sm text-red"
                v-if="state.generalServerError">
                {{ state.generalServerError }}
              </div>

              <div class="w-full flex justify-center">
                <div
                  class="w-44 h-9 flex items-center justify-center cursor-pointer rounded bg-red hover:bg-dark-red text-white transition-colors duration-300"
                  :class="{ 'pointer-events-none grayscale': state.isButtonDisabled }"
                  @click="handleSubmit">
                  Contact Us
                </div>
              </div>
            </div>
          </div>

          <div
            v-if="state.isSuccessInfoVisible"
            class="w-full">
            <div class="w-full text-xl font-medium text-center text-red mb-3">Thanks!</div>
            <div class="w-full text-lg font-light leading-tight text-center text-red mb-5">Someone from our sales team will reach out to you.</div>
            <div class="w-full flex justify-center">
              <img
                :src="GLOBAL_CURRENT_COMPANY_LOGO"
                :alt="`${GLOBAL_CURRENT_COMPANY_TITLE} logo`"
                :title="`${GLOBAL_CURRENT_COMPANY_TITLE} logo`"
                class="lazy block w-44 h-auto"
                loading="lazy" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, reactive, onUnmounted } from 'vue';
import InputText from '@/components/website/form-components/InputText.vue';
import InputEmail from '@/components/website/form-components/InputEmail.vue';
import InputPhoneNumber from '@/components/website/form-components/InputPhoneNumber.vue';
import TextAreaComponent from '@/components/website/form-components/TextAreaComponent.vue';

import GlobalHelper from '@/helpers/global/global.helper';
import DataLayerHelper from '@/helpers/global/dataLayer.helper';
import { WebsiteApiHelper } from '@/helpers/global/baseAPI.helper';
import AnalyticsHelper from '@/helpers/global/analytics.helper';
import LocalStorageHelper from '@/helpers/global/localStorage.helper';

const GLOBAL_CURRENT_COMPANY_LOGO = CURRENT_COMPANY_LOGO;
const GLOBAL_CURRENT_COMPANY_TITLE = CURRENT_COMPANY_TITLE;
const IA_MODAL_SUBMITTED_CK = 'IAModalVisibility_' + COMPANY_UID;
const IA_MODAL_SHOWS_COUNTER_CK = 'IAModalShowsCounter_' + COMPANY_UID;
const IA_MODAL_LATEST_SHOW_TS_CK = 'IAModalTimestamp_' + COMPANY_UID;
const CONSENT_MODE_WAS_ACCEPTED_CK = 'ConsentModeWasAccepted_' + COMPANY_UID;

const initialFormState = {
  fullName: '',
  email: '',
  phone: '',
  comments: '',
};

const initialValidationsState = {
  fullName: false,
  email: false,
  phone: false,
  comments: true,
};

const initialErrorsState = {
  fullName: '',
  email: '',
  phone: '',
  comments: '',
};

const state = reactive({
  isModalVisible: false,
  isSuccessInfoVisible: false,
  isButtonDisabled: false,
  generalServerError: '',
  triggerFormValidationCounter: 0,
  form: {
    ...initialFormState,
  },
  validations: {
    ...initialValidationsState,
  },
  errors: {
    ...initialErrorsState,
  },
  isDesktop: false,
});

const updateValidation = (name: keyof typeof state.validations, isValid: boolean) => {
  state.validations[name] = isValid;
};

const handleSubmit = async () => {
  state.triggerFormValidationCounter++;

  await new Promise((resolve) => setTimeout(resolve, 100));

  const isFormValid = Object.values(state.validations).every(Boolean);
  if (!isFormValid) return;

  state.isButtonDisabled = true;

  const [firstName, ...lastNameParts] = state.form.fullName.trim().split(' ');

  const storedVisitData = JSON.parse(LocalStorageHelper.getItem('first_visit_data') || '{}');

  const payload = {
    email: state.form.email,
    first_name: firstName,
    last_name: lastNameParts.join(' '),
    phone: state.form.phone,
    source_url: window.location.href,
    source_title: document.title,
    form_id: 1,
    comment: state.form.comments,
    ...storedVisitData,
  };

  try {
    const response = await WebsiteApiHelper.post('/leads/', payload);

    if (response.status >= 200 && response.status < 300) {
      state.isSuccessInfoVisible = true;
      GlobalHelper.setCookie(IA_MODAL_SUBMITTED_CK, '1', 24);
      DataLayerHelper.send('pop_up_form_submit', 'Lead Generation', 'Submit button', 'Pop up form', 1);
      AnalyticsHelper.formSubmitEvent('Immediate Assistance Pop Up Form');
    } else {
      throw response.data;
    }
  } catch (error: any) {
    state.isButtonDisabled = false;

    if (typeof error === 'object') {
      for (const field in error) {
        if (state.errors.hasOwnProperty(field)) {
          state.errors[field] = error[field][0];
        } else {
          state.generalServerError = Object.values(error).join(', ') || 'An error occurred while sending the form data. Please try again later.';
        }
      }
    } else {
      state.generalServerError = 'An error occurred while sending the form data. Please try again later.';
    }
  }
};

const closeModal = () => {
  state.isModalVisible = false;

  if (state.isSuccessInfoVisible) {
    setTimeout(() => {
      state.isSuccessInfoVisible = false;
      state.isButtonDisabled = false;
      state.generalServerError = '';
      state.form = { ...initialFormState };
      state.validations = { ...initialValidationsState };
      state.errors = { ...initialErrorsState };
    }, 500);
  }
};

const showModal = () => {
  const openButton = document.querySelector('.tutco-immediate-assistance-modal-open-button');

  openButton?.addEventListener('click', () => {
    state.isModalVisible = true;
  });

  if (typeof IS_POPUP_VISIBLE === 'undefined' || !IS_POPUP_VISIBLE || !IS_ASSIST_POP_UP_VISIBLE) return;

  openButton.classList.add('opacity-100', 'pointer-events-auto');

  let popupDelay = typeof POPUP_DELAY_SECONDS === 'number' ? POPUP_DELAY_SECONDS * 1000 : 7000;
  if (GlobalHelper.readCookie(CONSENT_MODE_WAS_ACCEPTED_CK) !== null) {
    popupDelay = 0;
    GlobalHelper.removeCookie(CONSENT_MODE_WAS_ACCEPTED_CK);
  }
  const resetPeriodMs = (POPUP_RESET_PERIOD_IN_HOURS ?? 24) * 60 * 60 * 1000;

  if (!GlobalHelper.readCookie(IA_MODAL_LATEST_SHOW_TS_CK)) {
    GlobalHelper.setCookie(IA_MODAL_LATEST_SHOW_TS_CK, String(Date.now()));
  }

  if (!GlobalHelper.readCookie(IA_MODAL_SHOWS_COUNTER_CK)) {
    GlobalHelper.setCookie(IA_MODAL_SHOWS_COUNTER_CK, '1');
  }

  const timestampNotExpired = Number(GlobalHelper.readCookie(IA_MODAL_LATEST_SHOW_TS_CK)) + resetPeriodMs > Date.now();
  const showLimitNotReached = Number(GlobalHelper.readCookie(IA_MODAL_SHOWS_COUNTER_CK)) <= POPUP_SHOW_LIMIT_PER_PERIOD;
  const notYetSubmitted = GlobalHelper.readCookie(IA_MODAL_SUBMITTED_CK) === null;

  if (!timestampNotExpired) {
    GlobalHelper.setCookie(IA_MODAL_SHOWS_COUNTER_CK, '1');
    GlobalHelper.setCookie(IA_MODAL_LATEST_SHOW_TS_CK, String(Date.now()));
  }

  if (timestampNotExpired && showLimitNotReached && notYetSubmitted) {
    setTimeout(() => {
      GlobalHelper.setCookie(IA_MODAL_SHOWS_COUNTER_CK, String(Number(GlobalHelper.readCookie(IA_MODAL_SHOWS_COUNTER_CK)) + 1));
      state.isModalVisible = true;
    }, popupDelay);
  }
};

const checkIsDesktop = () => {
  state.isDesktop = window.innerWidth >= 1280;
};

const updateCounterOnConsentActions = () => {
  const consentAcceptButtons = document.querySelectorAll('#btn-accept-all, #btn-accept-some');
  consentAcceptButtons.forEach((button) => {
    button.addEventListener('click', () => {
      if (Number(GlobalHelper.readCookie(IA_MODAL_SHOWS_COUNTER_CK)) <= POPUP_SHOW_LIMIT_PER_PERIOD && state.isModalVisible) {
        GlobalHelper.setCookie(IA_MODAL_SHOWS_COUNTER_CK, String(Number(GlobalHelper.readCookie(IA_MODAL_SHOWS_COUNTER_CK)) - 1));
        GlobalHelper.setCookie(CONSENT_MODE_WAS_ACCEPTED_CK, 'true');
      }
    });
  });
};

onMounted(() => {
  checkIsDesktop();
  showModal();
  updateCounterOnConsentActions();
  window.addEventListener('resize', checkIsDesktop);
});

onUnmounted(() => {
  window.removeEventListener('resize', checkIsDesktop);
});
</script>
